'use client';
import { useState, useRef } from 'react';

import { Meeting, MeetingIssue, MeetingReport } from '@/types.d';
import { useCompassContext } from '@/src/libs/CompassContext';
import { submitMeetingReport } from '@/src/utils/info-window-utils/submitMeetingReport';
import { handleErrorLogging } from '@/src/helpers/handleErrorLogging';
import { composeReportPrompt } from '@/src/helpers/reportContent';
import { meetingIssues } from '@/src/defaults/selectsDefaults';

import Heading from '../Typography/Heading';
import Paragraph from '../Typography/Paragraph';
import IconCloseButton from '../UI/IconCloseButton';
import Button from '../UI/Button';
import TextArea from '../UI/Textarea';
import Select from '../UI/Select';
import ErrorMessage from '../UI/ErrorMessage';

interface ReportCardProps {
  onClose: () => void;
  meeting: Meeting;
}

const ReportCard: React.FC<ReportCardProps> = ({
  onClose,
  meeting,
}): JSX.Element => {
  const [issue, setIssue] = useState<{ label: string; value: string }>(
    meetingIssues[0]
  );
  const [noteError, setNoteError] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const noteRef = useRef<HTMLTextAreaElement>(null);

  const ctx = useCompassContext();

  const handleSubmitReport = () => {
    setNoteError(null);
    setError(null);
    if (!ctx) {
      handleErrorLogging(
        new Error('Error submitting report. Please try again.'),
        setError
      );
      return;
    }
    if (!noteRef.current?.value) {
      setNoteError(
        'Please provide us with some details so we can fix this issue.'
      );
      return;
    }
    const submitReport = async () => {
      const report: MeetingReport = {
        meetingId: meeting.id,
        meetingName: meeting.name,
        issue: issue.value as keyof MeetingIssue,
        note: noteRef.current?.value || '',
      };
      try {
        await submitMeetingReport(report, ctx);
        setShowThankYou(true);
        setTimeout(() => {
          onClose();
        }, 3000);
      } catch (error) {
        handleErrorLogging(error as Error, setError);
        setTimeout(() => {
          onClose();
        }, 3000);
      }
    };
    submitReport();
  };

  return (
    <form
      name="report-meeting"
      aria-label="report-meeting"
      className="relative"
    >
      <span className="absolute top-0 right-0">
        <IconCloseButton
          ariaLabel="Close meeting report form"
          onClick={(e) => {
            e.preventDefault();
            onClose();
          }}
          title="Close report"
        />
      </span>
      {showThankYou ? (
        <div aria-live="polite">
          <Heading level="h3" text="Thank you!" />
          <Paragraph
            style="text-sm"
            text="Your report has been submitted, and will be reviewed by our staff. Thank you for helping us improve our database."
          />
        </div>
      ) : error ? (
        <div aria-live="polite">
          <Heading level="h3" text="Uh oh." />
          <Paragraph
            style="text-sm"
            text="Something went wrong. Please try again, or contact us directly."
          />
          <ErrorMessage error={error} />
        </div>
      ) : (
        <div aria-live="polite">
          <Heading level="h3" text="Report a problem" />
          <Paragraph style="mb-4" text={composeReportPrompt(meeting.name)} />
          <div className="mb-4">
            <Select
              id={`issue-${meeting.id}`}
              label="Issue"
              options={meetingIssues}
              setState={setIssue}
              value={issue}
            />
          </div>
          <div className="mb-4">
            <TextArea
              errorMsg={noteError}
              label="Details"
              id="report-details"
              maxLength={200}
              placeholder="Please provide details here - how should we fix this issue?"
              passRef={noteRef}
              required
            />
          </div>
          <Button
            ariaLabel="Submit meeting report"
            onClick={(e) => {
              e.preventDefault();
              handleSubmitReport();
            }}
            title="Submit"
          />
        </div>
      )}
    </form>
  );
};

export default ReportCard;
